import { pipe } from 'fp-ts/function';
import * as Opt from 'fp-ts/Option';
import { match } from 'ts-pattern';

import { applyNBANaming } from 'modules/ai/nba/utils/transformCampaign';
import { useGenerateSaveTemplatePayload } from 'modules/campaigns/lib/hooks';
import { UpdateTemplatePayload } from 'modules/campaigns/lib/hooks/useGenerateSaveTemplatePayload';
import { GraphQLCampaign } from 'modules/campaigns/lib/types';
import { Locale } from 'modules/domains/template';
import { MessageMedium } from 'modules/graphql/types';
import { getContentEditor } from 'modules/templateEditor/common/utils';
import { EMAIL_SUBJECT_MEDIUM_DATA_KEY } from 'modules/templateEditor/EditEmail/formFields/SubjectAndPreheaderSection';
import { PUSH_MESSAGE_MEDIUM_DATA_KEY } from 'modules/templateEditor/EditPush/formFields/BodyTextArea';
import { SMS_MESSAGE } from 'modules/templateEditor/EditSMS/formFields/MessageTextArea';
import {
  transformTemplateData,
  UseTemplateEditSingleContentById,
} from 'modules/templateEditor/hooks/useTemplateSingleContentEditById';
import { CommonTemplateLocaleData } from '../types';

export const createMediumContentUpdate = (medium: MessageMedium, suggestionContent: string) =>
  match(medium)
    .with(MessageMedium.Email, () => ({ [EMAIL_SUBJECT_MEDIUM_DATA_KEY]: suggestionContent }))
    .with(MessageMedium.SMS, () => ({ [SMS_MESSAGE]: suggestionContent }))
    .with(MessageMedium.Push, () => ({ [PUSH_MESSAGE_MEDIUM_DATA_KEY]: suggestionContent }))
    .otherwise(() => ({}));

export const updateTemplateFields = (
  campaignName: string,
  templateData: UpdateTemplatePayload,
  medium: MessageMedium,
  suggestion: string,
): UpdateTemplatePayload => {
  const templateContent = templateData.content;
  return {
    ...templateData,
    name: applyNBANaming(campaignName),
    content: {
      ...templateContent,
      ...createMediumContentUpdate(medium, suggestion),
    },
  };
};

export function createTransformTemplate(
  generatePayload: ReturnType<typeof useGenerateSaveTemplatePayload>,
  suggestion: string,
  wysiwygEditorEnabled: boolean,
  isRokuTemplatingOn: boolean,
  templateLocaleData: Opt.Option<CommonTemplateLocaleData>,
) {
  return (campaignData: GraphQLCampaign, templateData: UseTemplateEditSingleContentById) => {
    const medium = campaignData.messageMedium;

    const newLocaleData = pipe(
      templateLocaleData,
      Opt.map<CommonTemplateLocaleData, { projectLocales: Locale[]; localeToSet: string }>(
        (localeData) => {
          return {
            projectLocales: localeData.projectLocales as Locale[],
            localeToSet: Opt.getOrElse(() => '')(localeData.templateDefaultLocaleToSet),
          };
        },
      ),
      Opt.getOrElse(() => ({
        projectLocales: [] as Locale[],
        localeToSet: '',
      })),
    );

    const transformedSerializedTemplateData = transformTemplateData(
      templateData,
      campaignData as any,
      {
        /*
          These will be resolved from the refetch called from the fetch hook
          and are not monitored by this hook.
        */
        refetch: () => null,
        previousData: {},
        errorMessage: undefined,
        hasError: false,
        isLoading: false,
        isRokuTemplatingOn,
      },
    );

    const parsedTemplateData = JSON.parse(transformedSerializedTemplateData.results || '');

    const contentEditor = getContentEditor(
      medium,
      parsedTemplateData.mediumData.htmlEditor,
      !!parsedTemplateData.mediumData.beeBuilderMetadata,
      parsedTemplateData.mediumData.html,
      wysiwygEditorEnabled,
    );

    const generatedPayload = generatePayload(
      parsedTemplateData,
      medium,
      newLocaleData.projectLocales,
      newLocaleData.localeToSet,
      contentEditor,
    );

    return updateTemplateFields(campaignData.name, generatedPayload, medium, suggestion);
  };
}
